'use client';
// ═══════════════════════════════════════════════════════════════════════════
// LA LANGUE D'ARRIVÉE, SANS CASSER L'HYDRATATION
// ───────────────────────────────────────────────────────────────────────────
// 📏 LE DÉFAUT, mesuré au navigateur le 02/09/2026 (contexte neuf, donc sans
// langue retenue — seul `?lang=nl` déclenche) :
//     /pricing?lang=nl ............... React #418 × 1
//     /legal/privacy?lang=nl ......... React #418 × 1
//     /legal/cgu-employe?lang=nl ..... React #418 × 1
//     /blog/ouderschapsverlof-belgie . propre
//
// #418 = « le texte rendu par le serveur ne correspond pas à celui du client ».
// La cause est structurelle et non accidentelle : ces pages sont pré-rendues
// STATIQUEMENT, donc le serveur ne voit AUCUNE query string — il rend « fr ».
// Le client, lui, lit `?lang=nl` dès le premier rendu et écrit « nl ». Les deux
// divergent, React jette tout et refait le rendu.
//
// ⚠️ LE BLOG N'A PAS CE DÉFAUT, et la raison dit quoi faire : il déduit sa
// langue du CHEMIN (`usePathname`), que le serveur connaît aussi. Une query
// string, non. Quand la source n'existe pas au rendu serveur, on ne peut pas
// la lire au premier rendu client — on la lit APRÈS le montage.
//
// ⚠️ CE N'EST PAS MOI QUI L'AI INTRODUIT — `/pricing` l'avait depuis le
// 29/08 — mais je venais de propager le mécanisme à deux pages de plus. Livrer
// une erreur React sur des pages qui n'en avaient pas serait en dessous de ce
// que mes propres tests exigent (ils échouent sur toute plainte de React).
import { useState, useEffect, useCallback } from 'react';
import { langueArrivee, lireLangueRetenue, retenirLangue } from '@/app/lib/langue-arrivee';

/**
 * @param {string[]} disponibles  les langues réellement servies par la page
 * @param {string} [defaut]       ce que rend le SERVEUR — doit rester stable
 * @returns {[string, function]}  la langue et son setter, comme `useState`
 */
export function useLangueArrivee(disponibles, defaut = 'fr') {
  // Premier rendu : la MÊME valeur des deux côtés. C'est ce qui supprime #418.
  const [lang, poser] = useState(defaut);

  // ⚠️ IL N'Y A QUE DEUX ENDROITS QUI ÉCRIVENT, ET TOUS DEUX CONNAISSENT LA
  // VALEUR QU'ILS ÉCRIVENT. La 1re version avait un second `useEffect([lang])`
  // gardé par un drapeau `monte` : ça ne marchait pas, et c'est le test qui l'a
  // prouvé. Les effets d'un même commit s'exécutent dans l'ordre — le drapeau
  // était donc DÉJÀ posé quand le second effet tournait, et celui-ci lisait un
  // `lang` encore périmé (le défaut). Il écrivait « fr » par-dessus le choix
  // mémorisé. Un démontage à cet instant (une navigation) laissait « fr »
  // derrière lui : le visiteur retrouvait du français à sa visite suivante.
  //
  // 🔑 La leçon vaut au-delà d'ici : un effet gardé par un drapeau ne protège
  // pas d'un ÉTAT périmé. On n'écrit que des valeurs qu'on vient de calculer.
  useEffect(() => {
    const l = langueArrivee({
      recherche: window.location.search,
      memorisee: lireLangueRetenue(),
      disponibles,
      defaut,
    });
    poser(l);
    retenirLangue(l);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Le sélecteur de la page passe par ici : il écrit ce qu'il vient de choisir.
  const setLang = useCallback((l) => { poser(l); retenirLangue(l); }, []);

  return [lang, setLang];
}
